import React, { FC } from 'react';
import { Typography, Stack, Divider } from '@mui/material';
import Popup from 'src/components/content/popup/popup';
import ContentWrapper from 'src/components/content/content-wrapper';
import { PaymentMethod, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import styles from './modal.module.scss';
import ContentSection from 'src/components/content/content-section';
import colors from 'src/styles/equalution/colors';
import { Button } from 'reactstrap';
import { useAppSelector } from 'src/store/hooks';
import AfterpayIcon from 'src/assets/icons/afterpay.svg';
import PaymentPopup from 'src/pages/account/sections/subscription/update-payment/update-payment-popup';
import { PaymentMethodSelector } from '../components/PaymentMethodSelector';
import { usePaymentMethodState } from '../hooks/usePaymentMethodState';
import { ActionButton } from 'src/components/action-button';
import { getCurrencySymbol } from 'src/lib/utils';
import { isCardPaymentMethod } from '../utils/challengeAddon.util';

interface Props {
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  paymentMethod?: PaymentMethod;
  stripePromise: Promise<Stripe | null>;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  price: number;
  onPaymentTypeChange?: (type: 'card' | 'afterpay') => void;
}

export const PersonalCoachPaymentModal: FC<Props> = ({
  onClose,
  onConfirm,
  isLoading,
  paymentMethod,
  stripePromise,
  setPaymentMethod,
  price,
  onPaymentTypeChange,
}) => {
  const { user } = useAppSelector(state => state.auth);

  const {
    selectedPaymentType,
    paymentPopupVisible,
    setPaymentPopupVisible,
    handlePaymentTypeChange,
    handlePaymentMethodUpdate,
    handleAddCardClick,
  } = usePaymentMethodState({
    initialPaymentMethod: paymentMethod,
    onPaymentTypeChange,
  });

  return (
    <>
      <Popup onClose={onClose}>
        <ContentWrapper className={styles.background}>
          <ContentSection className={styles.content} collapseAt="sm">
            <Button className={styles.close} onClick={onClose} />

            <Typography
              mt={3}
              fontSize={26}
              fontWeight={700}
              color={colors.OFF_BLACK}
              lineHeight={'42px'}
              textAlign="center"
              paddingX={'32px'}
              gutterBottom>
              Choose Payment Method
            </Typography>

            <PaymentMethodSelector
              title=""
              isLoading={isLoading}
              selectedPaymentType={selectedPaymentType}
              onPaymentTypeChange={handlePaymentTypeChange}
              paymentMethod={paymentMethod}
              onAddCardClick={handleAddCardClick}
            />

            <Divider sx={{ my: 2 }} />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 8 }}>
              <Typography fontWeight={600}>Total</Typography>
              <Stack>
                <Typography fontWeight={600} fontSize={16} color={colors.SALAD}>
                  {getCurrencySymbol() + price.toFixed(2)}
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={16}
                  color={colors.OFF_BLACK}>
                  billed monthly
                </Typography>
              </Stack>
            </Stack>

            <Stack alignItems={'center'}>
              <ActionButton
                isLoading={isLoading}
                disabled={
                  selectedPaymentType === 'card' &&
                  !isCardPaymentMethod(paymentMethod)
                }
                onClick={onConfirm}>
                {selectedPaymentType === 'afterpay' ? (
                  <>
                    <img
                      src={AfterpayIcon}
                      alt="Afterpay"
                      style={{ width: '20px', height: '20px' }}
                    />
                    Finalise payment
                  </>
                ) : (
                  'Finalise payment'
                )}
              </ActionButton>
            </Stack>
          </ContentSection>
        </ContentWrapper>
      </Popup>

      {paymentPopupVisible && !!user && (
        <Elements stripe={stripePromise}>
          <PaymentPopup
            user={user}
            setPaymentMethod={method =>
              handlePaymentMethodUpdate(method, setPaymentMethod)
            }
            setPopupVisible={setPaymentPopupVisible}
          />
        </Elements>
      )}
    </>
  );
};
